import { Handle, type NodeProps, Position } from '@xyflow/react'
import React, { type FC, type MouseEventHandler, type ReactNode, useState } from 'react'
import classNames from 'classnames'
import { observer } from 'mobx-react-lite'
import { Icon, IconButton, type IIconProps, Tooltip, Typography } from 'shared/ui'
import { Actions } from './Actions/Actions'
import styles from './styles.module.scss'

type IActionNodeProp = {
  iconProps?: IIconProps | null
  title: string
  content: ReactNode
  isActive?: boolean
  placeholder?: string
  onClick?: MouseEventHandler<HTMLDivElement>
  hideActions?: boolean
  isError?: boolean
  ActionsContent?: FC<{ setOpen: (value: boolean) => void }>
} & NodeProps

export const FlowActionNode = observer(
  ({
    content,
    title,
    iconProps,
    placeholder,
    onClick,
    isActive,
    isError,
    ActionsContent,
  }: IActionNodeProp) => {
    const [open, setOpen] = useState(false)

    return (
      <>
        <Handle
          type='target'
          position={Position.Top}
          style={{ background: 'transparent', border: 'none', outline: 'none' }}
        />
        <div
          className={classNames(styles.wrap, {
            [styles.active]: isActive,
            [styles.actionsOpen]: open,
            [styles.error]: isError,
          })}
          onClick={onClick}
        >
          <div className={styles.header}>
            <div className={styles.left}>
              <Icon fontSize={20} {...iconProps} />
              <Typography ariaLabel={title} variant={'body-rg-medium'} ellipsis>
                {title}
              </Typography>
              {isError && (
                <div className={styles.box20}>
                  <Tooltip label={'Configuration required'} placement={'top'}>
                    <Icon icon={'alertTriangleYellow'} />
                  </Tooltip>
                </div>
              )}
            </div>

            {ActionsContent && (
              <div className={styles.actions} onClick={(event) => event.stopPropagation()}>
                <IconButton
                  ariaLabel={'more'}
                  icon={'moreHorizontal'}
                  color={'tertiary'}
                  variant={'icon'}
                  size={'small'}
                  onClick={() => {
                    setOpen(!open)
                  }}
                  active={open}
                />
                {open && <Actions setOpen={setOpen} Content={ActionsContent} />}
              </div>
            )}
          </div>
          <div className={classNames(styles.row, styles.content)}>{content}</div>
          <div className={classNames(styles.row, styles.placeholder)}>
            <Typography ariaLabel={'placeholder'} variant={'body-md-regular'} color={'tertiary'}>
              {placeholder}
            </Typography>
          </div>
        </div>
        <Handle
          type='source'
          position={Position.Bottom}
          style={{ background: 'transparent', border: 'none', outline: 'none' }}
        />
      </>
    )
  }
)
